import { isFocusVisible } from "@react-aria/interactions";
import { useCallback, useEffect, useRef, useState, type FocusEvent, type PointerEvent, type RefObject } from "react";
import type { PressEvent } from "react-aria-components";

// The prototype's pace, a Radix tooltip's: the card opens 300 ms into a hover, and the next trigger hovered within
// 300 ms of a close opens its card at once.
const OPEN_DELAY = 300;
const WARM_WINDOW = 300;
// Radix's grace area padding: how far the corridor reaches back into the element the pointer left.
const CORRIDOR_PADDING = 5;

const TABBABLE = "a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])";

type Point = [x: number, y: number];

// One card at a time across the page: opening a card closes the one before it.
let closeActiveCard: (() => void) | null = null;
let warmUntil = 0;
// The corridor the pointer is crossing between the open card and its trigger. While it is set, no other trigger opens.
let transit: { close: () => void; corridor: Point[] } | null = null;

const cross = ([ox, oy]: Point, [ax, ay]: Point, [bx, by]: Point) => (ax - ox) * (by - oy) - (ay - oy) * (bx - ox);

// Andrew's monotone chain: the convex hull, every corner turning the same way, so `isInside` checks one sign.
const getHull = (points: Point[]) => {
    const sorted = [...points].sort(([ax, ay], [bx, by]) => ax - bx || ay - by);
    const chain = (ordered: Point[]) => {
        const hull: Point[] = [];

        for (const point of ordered) {
            while (hull.length > 1 && cross(hull[hull.length - 2], hull[hull.length - 1], point) <= 0) {
                hull.pop();
            }
            hull.push(point);
        }

        return hull.slice(0, -1);
    };

    return [...chain(sorted), ...chain([...sorted].reverse())];
};

const isClick = ({ pointerType }: PressEvent) => pointerType === "mouse" || pointerType === "pen";

const isInside = (point: Point, hull: Point[]) => hull.every((corner, index) => cross(corner, hull[(index + 1) % hull.length], point) >= 0);

// Radix's grace area: the hull of `to` and the exit point, widened back into the element on the side the pointer left
// through, the side nearest the exit point (ties go left, right, top, bottom, as in Radix).
const getCorridor = ({ clientX: x, clientY: y, currentTarget }: PointerEvent, to: Element): Point[] => {
    const exit = currentTarget.getBoundingClientRect();
    const { left, top, right, bottom } = to.getBoundingClientRect();
    const sides: [number, Point][] = [
        [Math.abs(exit.left - x), [1, 0]],
        [Math.abs(exit.right - x), [-1, 0]],
        [Math.abs(exit.top - y), [0, 1]],
        [Math.abs(exit.bottom - y), [0, -1]]
    ];
    const [, [dx, dy]] = sides.reduce((nearest, side) => (side[0] < nearest[0] ? side : nearest));
    const padding = CORRIDOR_PADDING;
    const widened: Point[] = dx
        ? [[x + dx * padding, y - padding], [x + dx * padding, y + padding]]
        : [[x - padding, y + dy * padding], [x + padding, y + dy * padding]];

    return getHull([...widened, [left, top], [right, top], [right, bottom], [left, bottom]]);
};

interface Options {
    triggerRef: RefObject<HTMLElement | null>;
    cardRef: RefObject<HTMLElement | null>;
    isDisabled?: boolean;
    // A trigger with no link of its own: its card holds the only links to its sources, so a press that is not a click
    // opens the card (a tap also closes it), since touch never hovers and a screen reader has no pointer.
    opensOnPress?: boolean;
}

// The prototype's Radix tooltip: a pointer moving onto the trigger or keyboard focus opens the card; leaving the trigger
// and the card other than through the corridor between them, a click, or Escape closes it.
export const useHoverCard = ({ triggerRef, cardRef, isDisabled = false, opensOnPress = false }: Options) => {
    const [isOpen, setIsOpen] = useState(false);
    const isOpenRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    // A pointer opens the card once per visit, so a press that closed it holds until the pointer leaves.
    const hasEnteredRef = useRef(false);
    // Each reason the card stays open: pointer over the trigger or card, a selection drag, keyboard focus.
    const holdRef = useRef({ trigger: false, card: false, selecting: false, keyboard: false });
    const isReturningFocusRef = useRef(false);

    const clearTimer = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
    }, []);

    const close = useCallback(() => {
        clearTimer();
        // An unmounted card gets no pointerleave, so its holds end with it.
        holdRef.current.card = false;
        holdRef.current.selecting = false;
        holdRef.current.keyboard = false;

        if (transit?.close === close) {
            transit = null;
        }

        if (!isOpenRef.current) {
            return;
        }

        isOpenRef.current = false;
        warmUntil = Date.now() + WARM_WINDOW;

        if (closeActiveCard === close) {
            closeActiveCard = null;
        }

        setIsOpen(false);

        // Focus left in the card would fall to the page when it unmounts; the trigger takes it back, and
        // that focus must not reopen the card.
        if (cardRef.current?.contains(document.activeElement)) {
            isReturningFocusRef.current = true;
            triggerRef.current?.focus();
            isReturningFocusRef.current = false;
        }
    }, [cardRef, clearTimer, triggerRef]);

    const open = useCallback(() => {
        clearTimer();

        if (isOpenRef.current) {
            return;
        }

        closeActiveCard?.();
        closeActiveCard = close;
        isOpenRef.current = true;
        setIsOpen(true);
    }, [clearTimer, close]);

    const closeUnlessHeld = useCallback(() => {
        const { trigger, card, selecting, keyboard } = holdRef.current;

        if (!trigger && !card && !selecting && !keyboard) {
            close();
        }
    }, [close]);

    const onTriggerPointerMove = useCallback((event: PointerEvent) => {
        if (event.pointerType === "touch") {
            return;
        }

        holdRef.current.trigger = true;

        if (transit?.close === close) {
            transit = null;
        }

        // A pointer crossing to another card's open card passes over this trigger without opening it.
        if (isDisabled || hasEnteredRef.current || transit) {
            return;
        }

        hasEnteredRef.current = true;

        if (isOpenRef.current || closeActiveCard || Date.now() < warmUntil) {
            open();

            return;
        }

        clearTimer();
        timerRef.current = setTimeout(open, OPEN_DELAY);
    }, [clearTimer, close, isDisabled, open]);

    const onTriggerPointerLeave = useCallback((event: PointerEvent) => {
        if (event.pointerType === "touch") {
            return;
        }

        holdRef.current.trigger = false;
        hasEnteredRef.current = false;
        // Cancels an open still waiting on its delay.
        clearTimer();

        if (isOpenRef.current && cardRef.current) {
            transit = { close, corridor: getCorridor(event, cardRef.current) };
        }
    }, [cardRef, clearTimer, close]);

    // A click closes the card, as on the prototype's trigger, and so does any press that follows a link.
    const onTriggerPressStart = useCallback((event: PressEvent) => {
        if (isClick(event) || (!opensOnPress && event.pointerType !== "touch")) {
            close();
        }
    }, [close, opensOnPress]);

    const onTriggerPress = useCallback((event: PressEvent) => {
        if (!opensOnPress || isDisabled || isClick(event)) {
            return;
        }

        if (event.pointerType === "touch" && isOpenRef.current) {
            close();
        } else {
            open();
        }
    }, [close, isDisabled, open, opensOnPress]);

    const onCardPointerEnter = useCallback((event: PointerEvent) => {
        if (event.pointerType === "touch") {
            return;
        }

        holdRef.current.card = true;

        if (transit?.close === close) {
            transit = null;
        }
    }, [close]);

    const onCardPointerLeave = useCallback((event: PointerEvent) => {
        if (event.pointerType === "touch") {
            return;
        }

        holdRef.current.card = false;

        if (isOpenRef.current && triggerRef.current) {
            transit = { close, corridor: getCorridor(event, triggerRef.current) };
        }
    }, [close, triggerRef]);

    // A drag that selects text can overshoot the card's edge; the card waits for the button's release.
    const onCardPointerDown = useCallback((event: PointerEvent) => {
        if (event.pointerType !== "mouse" || event.button !== 0) {
            return;
        }

        holdRef.current.selecting = true;
        window.addEventListener("pointerup", () => {
            holdRef.current.selecting = false;
        }, { once: true });
    }, []);

    const onFocus = useCallback(() => {
        if (isDisabled || isReturningFocusRef.current || !isFocusVisible()) {
            return;
        }

        holdRef.current.keyboard = true;
        open();
    }, [isDisabled, open]);

    const onBlur = useCallback((event: FocusEvent) => {
        const next = event.relatedTarget as Node | null;

        if (next && (triggerRef.current?.contains(next) || cardRef.current?.contains(next))) {
            return;
        }

        holdRef.current.keyboard = false;
        closeUnlessHeld();
    }, [cardRef, closeUnlessHeld, triggerRef]);

    // The popover reports a close when focus leaves the card or the page scrolls under the trigger.
    const onOpenChange = useCallback((next: boolean) => {
        if (!next) {
            close();
        }
    }, [close]);

    // Moving focus from the card back to the trigger is not leaving the card.
    const shouldCloseOnInteractOutside = useCallback((element: Element) => !triggerRef.current?.contains(element), [triggerRef]);

    useEffect(() => {
        if (isDisabled) {
            close();
        }
    }, [close, isDisabled]);

    useEffect(() => () => {
        clearTimer();

        if (closeActiveCard === close) {
            closeActiveCard = null;
        }

        if (transit?.close === close) {
            transit = null;
        }
    }, [clearTimer, close]);

    // Away from the trigger and the card, the pointer keeps the card only inside the corridor, and a press anywhere
    // else closes it.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const isOnTriggerOrCard = (target: EventTarget | null) => !!target && !!(triggerRef.current?.contains(target as Node) || cardRef.current?.contains(target as Node));

        const onPointerMove = (event: globalThis.PointerEvent) => {
            const { selecting, keyboard } = holdRef.current;

            if (event.pointerType === "touch" || selecting || keyboard || isOnTriggerOrCard(event.target)) {
                return;
            }

            if (transit?.close === close && isInside([event.clientX, event.clientY], transit.corridor)) {
                return;
            }

            close();
        };

        const onPointerDown = (event: globalThis.PointerEvent) => {
            if (!isOnTriggerOrCard(event.target)) {
                close();
            }
        };

        document.addEventListener("pointermove", onPointerMove);
        document.addEventListener("pointerdown", onPointerDown, true);

        return () => {
            document.removeEventListener("pointermove", onPointerMove);
            document.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, [cardRef, close, isOpen, triggerRef]);

    // The card is portalled to the end of the page, so Tab is routed by hand. Window capture runs before the
    // popover's FocusScope on document, which would route Tab from whatever had focus when the card opened.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                close();

                return;
            }

            const trigger = triggerRef.current;
            const tabbables = Array.from(cardRef.current?.querySelectorAll<HTMLElement>(TABBABLE) ?? []);

            if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey || !trigger || tabbables.length === 0) {
                return;
            }

            const active = document.activeElement;

            if (active === trigger && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                tabbables[0].focus();
            } else if (active === tabbables[0] && event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                trigger.focus();
            } else if (active === tabbables[tabbables.length - 1] && !event.shiftKey) {
                // The browser's Tab then moves on from the trigger.
                trigger.focus();
            }
        };

        window.addEventListener("keydown", onKeyDown, true);

        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [cardRef, close, isOpen, triggerRef]);

    return {
        isOpen,
        triggerProps: {
            onPointerMove: onTriggerPointerMove,
            onPointerLeave: onTriggerPointerLeave,
            onPressStart: onTriggerPressStart,
            onPress: onTriggerPress,
            onFocus,
            onBlur
        },
        popoverProps: {
            isOpen,
            onOpenChange,
            shouldCloseOnInteractOutside,
            onPointerEnter: onCardPointerEnter,
            onPointerLeave: onCardPointerLeave
        },
        cardProps: {
            onPointerDown: onCardPointerDown,
            onFocus,
            onBlur
        }
    };
};
